<template>
  <div class="file-input-box" @click="onBoxClick" :class="{ 'is-focused': isFocused }" tabindex="0" @focus="onFocus" @blur="onBlur">
    <label :for="inputId" class="file-input-label">
      <slot name="label">{{ label }}</slot>
    </label>
    <input
      ref="fileInput"
      :id="inputId"
      type="file"
      :accept="accept"
      :multiple="multiple"
      @change="onFileChange"
      @click.stop
      style="display: none;"
    />
    <div class="file-input-placeholder">
      <slot name="placeholder">{{ placeholder }}</slot>
    </div>
    <div v-if="fileName" class="file-input-filename">{{ truncateName(fileName) }}</div>
  </div>
</template>

<script>
export default {
  name: 'FileInputBox',
  props: {
    label: { type: String, default: 'Choose File' },
    placeholder: { type: String, default: 'Click or paste file here' },
    accept: { type: String, default: '' },
    multiple: { type: Boolean, default: false },
    value: { type: [File, Array], default: null },
    fieldKey: { type: String, required: true },
  },
  data() {
    return {
      fileName: '',
      isFocused: false,
      inputId: 'file-input-' + Math.random().toString(36).substr(2, 9),
      clickCount: 0,
    };
  },
  mounted() {
    this.registerField();
  },
  beforeDestroy() {
    this.unregisterField();
  },
  methods: {
    onBoxClick() {
      this.clickCount += 1;
      this.$emit('focus-field', this.fieldKey);
      // First click: just focus/select field for paste, do not open dialog
      if (this.clickCount === 1) {
        this.focusBox();
        return;
      }
      // Second and later clicks: open native file chooser
      this.$refs.fileInput.click();
    },
    onFocus() {
      this.isFocused = true;
      this.$emit('focus-field', this.fieldKey);
    },
    onBlur() {
      this.isFocused = false;
      // Reset click count when losing focus, so next click will be treated as first click again
      this.clickCount = 0;
      this.$emit('blur-field', this.fieldKey);
    },
    onFileChange(e) {
      const files = e.target.files;
      this.fileName = files && files.length ? files[0].name : '';
      this.$emit('input', this.multiple ? Array.from(files) : files[0]);
    },
    registerField() {
      if (!window.__globalFileInputRegistry) window.__globalFileInputRegistry = {};
      window.__globalFileInputRegistry[this.fieldKey] = this;
    },
    unregisterField() {
      if (window.__globalFileInputRegistry) {
        delete window.__globalFileInputRegistry[this.fieldKey];
      }
    },
    focusBox() {
      this.isFocused = true;
      this.$el.focus();
    },
    pasteFile(file) {
      const dt = new DataTransfer();
      dt.items.add(file);
      this.$refs.fileInput.files = dt.files;
      this.fileName = file.name;
      // Only emit input event - don't dispatch change event to avoid double triggers
      this.$emit('input', this.multiple ? [file] : file);
    },
    clearFile() {
      // Clear the file name and input value
      this.fileName = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
        // Clear files using DataTransfer
        const dt = new DataTransfer();
        this.$refs.fileInput.files = dt.files;
      }
    },
    truncateName(name) {
      return name.length > 10 ? name.substring(0, 10) + "..." : name;
    },
  },
};
</script>

<style scoped>
.file-input-box {
  border: 2px dashed #bbb;
  border-radius: 6px;
  padding: 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: border-color 0.2s;
  width: 100%;
}
.file-input-box.is-focused {
  border-color: #007bff;
}
.file-input-label {
  font-weight: bold;
  margin-bottom: 8px;
}
.file-input-placeholder {
  color: #888;
  font-size: 14px;
}
.file-input-filename {
  margin-top: 8px;
  color: #333;
  font-size: 13px;
}
</style>
